/**
 * NPEAllocationWizard — 2-step wizard cho NPE Allocation (SOP §7.8).
 *
 * Route: `/purchase/npe-fixations/:id/allocate?session={uid}`
 *
 * Flow:
 *  - Đọc `session` query param. Nếu không có, gọi POST /sessions/create
 *    { fixation_id } để tạo mới và replace URL.
 *  - Step 1: Input lines
 *      * Header (Allocated Information panel) — read-only.
 *      * Bảng lines — user nhập allocate_quantity / allocate_value mỗi row.
 *        Debounced 500ms -> PUT /sessions/{uid}/lines (auto-save).
 *      * Validate: sum(allocate_quantity) <= to_be_allocate_quantity.
 *      * Allocation Note (textarea).
 *  - Step 2: Review & Confirm
 *      * Hiển thị tổng kết: sum allocate_quantity, sum allocate_value,
 *        sum interest, num lines.
 *      * Nút "Cancel" -> DELETE session, navigate back.
 *      * Nút "Commit" -> POST /sessions/{uid}/commit -> success: navigate to
 *        /purchase/npe-fixations/{id} (or list) + snackbar.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import type { GridColDef } from '@mui/x-data-grid'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import ConfirmDialog from '@/components/ConfirmDialog'
import DataTableMUI from '@/components/DataTableMUI'
import DateTextField from '@/components/DateTextField'
import PageDataState from '@/components/PageDataState'
import { usePagePermission } from '@/hooks/usePagePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCancelAllocationSessionMutation,
  useCommitAllocationSessionMutation,
  useCreateAllocationSessionMutation,
  useGetAllocationSessionQuery,
  useUpdateSessionLinesMutation,
} from '@/store/api/npeAllocationsApi'
import type {
  NPEAllocationCommitIn,
  NPEAllocationLine,
  NPEAllocationLineIn,
  NPEAllocationSessionState,
} from '@/types/api'
import { formatDate } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'
import { formatNumber } from '@/utils/money'

interface LineInputState {
  paydata_p_id: number
  allocate_quantity: number | ''
  allocate_value: number | ''
}

const STEPS = ['Input lines', 'Review & Confirm']

export function NPEAllocationWizard() {
  const snack = useSnackbar()
  const { canEdit } = usePagePermission()
  const navigate = useNavigate()
  const { id: idParam = '' } = useParams<{ id: string }>()
  const fixationId = Number(idParam)
  const [searchParams, setSearchParams] = useSearchParams()
  const sessionUidParam = searchParams.get('session') ?? ''

  const [sessionUid, setSessionUid] = useState<string>(sessionUidParam)
  const [step, setStep] = useState(0)
  const [allocationNote, setAllocationNote] = useState<string>('')
  const [allocationDate, setAllocationDate] = useState<string>(
    new Date().toISOString().slice(0, 10),
  )
  const [confirmCancel, setConfirmCancel] = useState(false)

  // ---- Bootstrap session ----
  const [createSession, { isLoading: creatingSession }] =
    useCreateAllocationSessionMutation()

  useEffect(() => {
    if (sessionUid || !fixationId) return
    void (async () => {
      try {
        const res = await createSession({ fixation_id: fixationId }).unwrap()
        const uid = res.data?.session_uid
        if (!uid) throw new Error('Missing session_uid')
        setSessionUid(uid)
        setSearchParams({ session: uid }, { replace: true })
      } catch (err) {
        snack.error(getErrorMessage(err, 'Could not create session'))
        navigate(-1)
      }
    })()
  }, [sessionUid, fixationId, createSession, navigate, setSearchParams, snack])

  // ---- Load session state ----
  const {
    data: sessionRes,
    isLoading: loadingSession,
    error: sessionError,
  } = useGetAllocationSessionQuery(sessionUid, { skip: !sessionUid })
  const session = sessionRes?.data

  const [updateLines] = useUpdateSessionLinesMutation()
  const [commitSession, { isLoading: committing }] = useCommitAllocationSessionMutation()
  const [cancelSession, { isLoading: cancelling }] = useCancelAllocationSessionMutation()

  // ---- Local input state cho lines (mirror server, debounced sync) ----
  const [lineInputs, setLineInputs] = useState<Record<number, LineInputState>>({})

  // Init lineInputs khi session load lần đầu (hoặc paydata_p_id set thay đổi)
  const seededRef = useRef<string>('')
  useEffect(() => {
    if (!session) return
    const sig = session.lines.map((l) => l.paydata_p_id).join(',')
    if (seededRef.current === sig) return
    seededRef.current = sig
    const next: Record<number, LineInputState> = {}
    for (const ln of session.lines) {
      next[ln.paydata_p_id] = {
        paydata_p_id: ln.paydata_p_id,
        allocate_quantity:
          ln.allocate_quantity === null || ln.allocate_quantity === undefined
            ? ''
            : ln.allocate_quantity,
        allocate_value:
          ln.allocate_value === null || ln.allocate_value === undefined
            ? ''
            : ln.allocate_value,
      }
    }
    setLineInputs(next)
    setAllocationNote(session.allocation_note ?? '')
  }, [session])

  // ---- Debounced auto-save ----
  const saveTimerRef = useRef<number | null>(null)
  const scheduleSave = useCallback(
    (next: Record<number, LineInputState>) => {
      if (!sessionUid) return
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current)
      }
      saveTimerRef.current = window.setTimeout(() => {
        const lines: NPEAllocationLineIn[] = Object.values(next).map((l) => ({
          paydata_p_id: l.paydata_p_id,
          allocate_quantity: l.allocate_quantity === '' ? null : l.allocate_quantity,
          allocate_value: l.allocate_value === '' ? null : l.allocate_value,
        }))
        void updateLines({ sessionUid, lines })
          .unwrap()
          .catch((err) => {
            snack.error(getErrorMessage(err, 'Auto-save lines failed'))
          })
      }, 500)
    },
    [sessionUid, updateLines, snack],
  )

  useEffect(
    () => () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current)
    },
    [],
  )

  const handleLineChange = (
    paydataId: number,
    field: 'allocate_quantity' | 'allocate_value',
    raw: string,
  ) => {
    const value = raw === '' ? '' : Number(raw)
    setLineInputs((prev) => {
      const cur = prev[paydataId] ?? {
        paydata_p_id: paydataId,
        allocate_quantity: '',
        allocate_value: '',
      }
      const next = { ...prev, [paydataId]: { ...cur, [field]: value } }
      scheduleSave(next)
      return next
    })
  }

  // ---- Totals (real-time từ local inputs để UX) ----
  const totals = useMemo(() => {
    let qty = 0
    let val = 0
    let interest = 0
    let nLines = 0
    if (session) {
      for (const ln of session.lines) {
        const inp = lineInputs[ln.paydata_p_id]
        const q = typeof inp?.allocate_quantity === 'number' ? inp.allocate_quantity : 0
        const v = typeof inp?.allocate_value === 'number' ? inp.allocate_value : 0
        if (q > 0 || v > 0) nLines += 1
        qty += q
        val += v
        // Interest: server-computed; chỉ show khi đã save (có allocated_interest)
        interest += ln.allocated_interest ?? 0
      }
    }
    return { qty, val, interest, nLines }
  }, [session, lineInputs])

  // Validate
  const overAllocated = useMemo(() => {
    if (!session) return false
    const cap = session.to_be_allocate_quantity ?? 0
    return totals.qty > cap + 1e-6
  }, [session, totals])

  const canGoStep2 = !!session && !overAllocated && totals.nLines > 0

  // ---- Commit / Cancel ----
  const handleCommit = async () => {
    if (!sessionUid || !session) return
    const body: NPEAllocationCommitIn = {
      allocation_note: allocationNote || null,
      allocation_date: allocationDate || null,
      lines: Object.values(lineInputs).map((l) => ({
        paydata_p_id: l.paydata_p_id,
        allocate_quantity: l.allocate_quantity === '' ? null : l.allocate_quantity,
        allocate_value: l.allocate_value === '' ? null : l.allocate_value,
      })),
    }
    try {
      await commitSession({ sessionUid, body }).unwrap()
      snack.success(`Committed allocation for ${session.fixation_no}`)
      navigate(`/purchase/npe-fixations`)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to commit allocation'))
    }
  }

  const handleCancel = async () => {
    if (!sessionUid) {
      navigate(-1)
      return
    }
    try {
      await cancelSession(sessionUid).unwrap()
      snack.info('Allocation session cancelled')
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to cancel session'))
    }
    navigate(`/purchase/npe-fixations`)
  }

  // ---- Render ----
  if (creatingSession || loadingSession || !sessionUid) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <PageDataState loading={false} error={sessionError} empty={!session}>
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Tooltip title="Cancel and go back">
            <IconButton onClick={() => setConfirmCancel(true)}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" fontWeight={700}>
            NPE Allocation Wizard
          </Typography>
          {session && (
            <Typography variant="subtitle1" color="text.secondary">
              — {session.fixation_no}
            </Typography>
          )}
        </Box>

        <Paper sx={{ p: 2 }}>
          <Stepper activeStep={step} alternativeLabel sx={{ mb: 2 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {session && <AllocatedInfoPanel session={session} />}

          {overAllocated && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Total allocated quantity ({formatNumber(totals.qty, 0)}) exceeds
              the remaining to-be-allocated quantity ({formatNumber(session?.to_be_allocate_quantity ?? null, 0)}).
            </Alert>
          )}

          {step === 0 && session && (
            <Box mt={2} display="flex" flexDirection="column" gap={2}>
              <LinesTable
                session={session}
                inputs={lineInputs}
                onChange={handleLineChange}
              />
              <TextField
                label="Allocation Note"
                size="small"
                multiline
                minRows={2}
                fullWidth
                value={allocationNote}
                onChange={(e) => setAllocationNote(e.target.value)}
              />
            </Box>
          )}

          {step === 1 && session && (
            <Box mt={2} display="flex" flexDirection="column" gap={2}>
              <ReviewSummary totals={totals} session={session} />
              <DateTextField
                label="Allocation Date"
                size="small"
                value={allocationDate}
                onChange={setAllocationDate}
                sx={{ maxWidth: 240 }}
              />
              <TextField
                label="Allocation Note"
                size="small"
                multiline
                minRows={2}
                fullWidth
                value={allocationNote}
                onChange={(e) => setAllocationNote(e.target.value)}
              />
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
            <Button onClick={() => setConfirmCancel(true)} disabled={committing || cancelling}>
              Cancel
            </Button>
            {step === 1 && (
              <Button
                variant="outlined"
                onClick={() => setStep(0)}
                disabled={committing}
              >
                Back
              </Button>
            )}
            {step === 0 && (
              <Button
                variant="contained"
                color="primary"
                disabled={!canGoStep2}
                onClick={() => setStep(1)}
              >
                Next
              </Button>
            )}
            {step === 1 && (
              <Button
                variant="contained"
                color="warning"
                disabled={!canEdit || committing || overAllocated}
                onClick={() => void handleCommit()}
                startIcon={
                  committing ? <CircularProgress size={16} color="inherit" /> : undefined
                }
              >
                Commit
              </Button>
            )}
          </Stack>
        </Paper>

        <ConfirmDialog
          open={confirmCancel}
          title="Cancel Allocation"
          message="Are you sure you want to cancel this session? Any uncommitted entries will be discarded."
          destructive
          loading={cancelling}
          onConfirm={() => {
            setConfirmCancel(false)
            void handleCancel()
          }}
          onCancel={() => setConfirmCancel(false)}
        />
      </Box>
    </PageDataState>
  )
}

/** Allocated Information panel — read-only header (SOP Table 59). */
function AllocatedInfoPanel({ session }: { session: NPEAllocationSessionState }) {
  const cells: Array<[string, React.ReactNode]> = [
    ['Fixation No', session.fixation_no],
    ['NPE Contract', session.npe_contract ?? '—'],
    ['P Contract', session.p_contract ?? '—'],
    ['Supplier', session.supplier ?? '—'],
    ['Fixed Price', formatNumber(session.fixed_price ?? null, 2)],
    ['Fixed Quantity', formatNumber(session.fixed_quantity ?? null, 0)],
    ['Fixed Amount', formatNumber(session.fixed_amount ?? null, 0)],
    ['Allocated Qty', formatNumber(session.allocated_quantity ?? null, 0)],
    ['To Be Alloc Qty', formatNumber(session.to_be_allocate_quantity ?? null, 0)],
    ['Allocated Amount', formatNumber(session.allocated_amount ?? null, 0)],
    ['To Be Alloc Amount', formatNumber(session.to_be_allocate_amount ?? null, 0)],
  ]
  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} color="primary" mb={1}>
        Allocated Information
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={2}>
        {cells.map(([k, v]) => (
          <Box key={k} sx={{ flex: '1 1 180px', minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {k}
            </Typography>
            <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
              {v === '' || v === null || v === undefined ? '—' : v}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  )
}

/** Step 1 — bảng lines (advance candidates). */
function LinesTable({
  session,
  inputs,
  onChange,
}: {
  session: NPEAllocationSessionState
  inputs: Record<number, LineInputState>
  onChange: (
    paydataId: number,
    field: 'allocate_quantity' | 'allocate_value',
    raw: string,
  ) => void
}) {
  const rows = useMemo(
    () => session.lines.map((l) => ({ id: l.paydata_p_id, ...l })),
    [session.lines],
  )

  const columns = useMemo<GridColDef<NPEAllocationLine & { id: number }>[]>(
    () => [
      { field: 'advanced_no', headerName: 'Advance No', width: 130 },
      {
        field: 'advanced_date',
        headerName: 'Advanced Date',
        width: 130,
        valueGetter: (_v, row) => formatDate(row.advanced_date),
      },
      {
        field: 'advanced_kg',
        headerName: 'Advanced Kg',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.advanced_kg ?? null, 0),
      },
      {
        field: 'advanced_price',
        headerName: 'Advanced Price',
        width: 130,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.advanced_price ?? null, 2),
      },
      {
        field: 'requested_vnd',
        headerName: 'Requested VND',
        width: 140,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.requested_vnd ?? null, 0),
      },
      {
        field: '_alloc_qty',
        headerName: 'Allocate Qty',
        width: 140,
        renderCell: (p) => {
          const inp = inputs[p.row.paydata_p_id]
          return (
            <TextField
              size="small"
              type="number"
              value={inp?.allocate_quantity ?? ''}
              onChange={(e) => onChange(p.row.paydata_p_id, 'allocate_quantity', e.target.value)}
              sx={{ width: '100%' }}
            />
          )
        },
      },
      {
        field: '_alloc_val',
        headerName: 'Allocate Value',
        width: 150,
        renderCell: (p) => {
          const inp = inputs[p.row.paydata_p_id]
          return (
            <TextField
              size="small"
              type="number"
              value={inp?.allocate_value ?? ''}
              onChange={(e) => onChange(p.row.paydata_p_id, 'allocate_value', e.target.value)}
              sx={{ width: '100%' }}
            />
          )
        },
      },
      {
        field: 'interest_date_num',
        headerName: 'Interest Days',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.interest_date_num ?? null, 0),
      },
      {
        field: 'interest_rate',
        headerName: 'Interest Rate',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.interest_rate ?? null, 4),
      },
      {
        field: 'actual_payment_vnd',
        headerName: 'Actual Pay (VND)',
        width: 150,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.actual_payment_vnd ?? null, 0),
      },
      {
        field: 'allocated_value',
        headerName: 'Allocated Value',
        width: 140,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.allocated_value ?? null, 0),
      },
      {
        field: 'allocated_quantity',
        headerName: 'Allocated Qty',
        width: 130,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.allocated_quantity ?? null, 0),
      },
      {
        field: 'allocated_interest',
        headerName: 'Allocated Interest',
        width: 150,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.allocated_interest ?? null, 0),
      },
    ],
    [inputs, onChange],
  )

  return (
    <Paper variant="outlined">
      <DataTableMUI
        rows={rows}
        columns={columns as GridColDef[]}
        height={460}
        disableRowSelectionOnClick
      />
    </Paper>
  )
}

/** Step 2 — review summary card. */
function ReviewSummary({
  totals,
  session,
}: {
  totals: { qty: number; val: number; interest: number; nLines: number }
  session: NPEAllocationSessionState
}) {
  const cells: Array<[string, React.ReactNode]> = [
    ['Allocated lines', totals.nLines],
    ['Total allocate_quantity', formatNumber(totals.qty, 0)],
    ['Total allocate_value (VND)', formatNumber(totals.val, 0)],
    ['Total interest (VND, server)', formatNumber(totals.interest, 0)],
    ['Fixation', session.fixation_no],
    ['NPE / P Contract', `${session.npe_contract ?? '—'} / ${session.p_contract ?? '—'}`],
  ]
  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} color="primary" mb={1}>
        Review
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={2}>
        {cells.map(([k, v]) => (
          <Box key={k} sx={{ flex: '1 1 200px', minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {k}
            </Typography>
            <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
              {v === '' || v === null || v === undefined ? '—' : v}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  )
}

export default NPEAllocationWizard
